/**
 * L'onglet « Portrait » de la fiche d'un élève — chantier « manuel vivant »,
 * Φ-MV-5 (MV-TUT-03, MV-MAI-11, F-15, F-11).
 *
 * Le portrait COMPLET : ce que l'élève lit de lui-même (ses notions en paliers
 * et en comptes, sa préparation en états) et ce qui n'est servi QU'ICI — la
 * tendance à la baisse, la lecture tuteur des erreurs qui reviennent, et la
 * position face au programme (F-15 : elle vit ici, et seulement ici).
 *
 * ─── La charge (F-11, MV-UX-02) ─────────────────────────────────────────────
 *
 * TROIS sections ouvertes à l'arrivée, celles de la table `MV-UX-02` : la
 * maîtrise par notion, les erreurs relevées et la préparation aux évaluations —
 * ce que le tuteur vient chercher (revue UX `UX-7-12` : l'erreur qui revient et
 * l'évaluation qui approche demandaient deux clics). Dans « Erreurs relevées »,
 * seule la PREMIÈRE erreur est dépliée (cinq au plus, de la plus fréquente à la
 * moins fréquente) ; dans « Préparation », seule la PROCHAINE évaluation l'est,
 * et son plan reste replié. Les prérequis et la position face au programme sont
 * repliés, chacun avec un résumé qui dit ce qu'il y a derrière. Le choix du
 * tuteur est retenu par `SectionRabattable`, par utilisateur.
 *
 * ─── « Relevée » n'est pas « récurrente » (`UX-7-13`) ───────────────────────
 *
 * La section liste les erreurs RECONNUES dans les travaux, même une seule
 * fois. « Récurrente » est réservé à celles qui répondent à la définition du
 * produit (`DECISIONS-ARCHITECTE.md`, point 5), que le serveur marque une par
 * une : c'est aussi ce que compte le filtre « Erreur récurrente » de la liste.
 *
 * ─── Ce que cet écran ne montre pas ─────────────────────────────────────────
 *
 * Aucun pourcentage, aucune moyenne, aucun indice — MÊME ICI
 * (`DECISIONS-ARCHITECTE.md`, point 17). Un palier est un mot et quatre crans ;
 * une préparation est un état ; le reste est un dénombrement (« 4 réussis sur
 * 6 »). La seule proportion à l'écran est la pondération SCOLAIRE des deux
 * compétences (« 70 % du résultat »), donnée du programme écrite par le
 * serveur : ce n'est pas une mesure de l'élève. Aucun code interne non plus :
 * une notion se dit par son rang et son titre, une erreur par son intitulé.
 */
import {
  LIBELLES_PALIER_NOTION,
  NIVEAUX_EXERCICE_NOMMES,
  denombrer,
  type ErreurRecurrenteTuteurDto,
  type EtatPreparation,
  type PreparationEvaluationDto,
  type VueEleveTuteurResponse,
} from '@silex/shared';
import { Link } from 'react-router-dom';

import { Badge } from '../../components/Badge';
import { CransNotion } from '../../components/CransNotion';
import { SectionRabattable } from '../../components/SectionRabattable';
import { TexteMaths } from '../../components/TexteMaths';
import {
  PHRASE_AUCUNE_ERREUR,
  dansCombien,
  direActivite,
  direEtatAttente,
  direPreuveAttente,
  jourCivil,
  nomDeNiveau,
  nomDeNotion,
  resumeErreurs,
  resumeMaitrise,
  resumePosition,
  resumePreparations,
  resumePrerequis,
} from '../../lib/manuel-tuteur-format';
import { adresses } from '../../lib/savoir-format';
import type { BadgeTone } from '../../lib/suivi-format';
import { formatDate } from '../../lib/tuteur-format';

/** L'état d'une préparation → le ton de sa pastille. Jamais de rouge : « à renforcer » est en ardoise. */
const TON_PREPARATION: Readonly<Record<EtatPreparation, BadgeTone>> = {
  pret: 'acquis',
  en_bonne_voie: 'en-cours',
  a_renforcer: 'a-revoir',
  a_commencer: 'a-revoir',
};

const cle = (section: string): string => `manuel.portrait.${section}`;

export function PortraitTuteur({ vue }: { readonly vue: VueEleveTuteurResponse }) {
  const { chapitre } = vue;
  const rang = chapitre.rang;
  const aucunTravail = vue.notions.every((n) => !n.travaillee);

  return (
    <div className="stack" data-testid="manuel-portrait">
      {/* ── 1. La maîtrise par notion ─────────────────────────────────────── */}
      <div data-testid="manuel-maitrise">
        <SectionRabattable
          titre="Maîtrise par notion"
          resume={resumeMaitrise(vue.notions)}
          sous={`${direActivite(vue.activite)} · au ${jourCivil(vue.aujourdhui)}`}
          cleMemoire={cle('maitrise')}
        >
          {aucunTravail ? (
            <p className="note-line">
              <strong>Aucun travail enregistré.</strong> Le portrait se construit dès le premier
              exercice. Une séquence déposée lui donne un point de départ.
            </p>
          ) : null}
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col">Notion</th>
                  <th scope="col">Palier</th>
                  <th scope="col">Niveau conseillé</th>
                  <th scope="col">Travaux</th>
                </tr>
              </thead>
              <tbody>
                {vue.notions.map((n) => (
                  <tr key={n.notion.code}>
                    <td>
                      <strong>{nomDeNotion(n.notion, rang)}</strong>
                      {n.ciblee ? (
                        <span className="tag tag--acc tag--suite">À approfondir</span>
                      ) : null}
                      <span className="cell-sub">
                        {n.derniereActiviteLe === null
                          ? 'Pas encore travaillée'
                          : `Dernier travail le ${formatDate(n.derniereActiviteLe)}`}
                      </span>
                    </td>
                    <td>
                      {n.travaillee ? (
                        <CransNotion palier={n.palier} libelle={n.libellePalier} />
                      ) : (
                        <span className="cell-sub">Pas encore travaillée</span>
                      )}
                    </td>
                    <td>
                      {n.repere === null ? (
                        <span className="cell-sub">—</span>
                      ) : (
                        <>
                          {nomDeNiveau(n.repere.niveau)}
                          <span className="cell-sub">
                            {denombrer(n.repere.reussites, 'réussite')} sur {n.repere.sur} à ce
                            niveau
                          </span>
                        </>
                      )}
                    </td>
                    <td>
                      {n.essais === 0 ? (
                        <span className="cell-sub">Aucun</span>
                      ) : (
                        <>
                          {denombrer(n.reussites, 'réussi')} sur {n.essais}
                          <span className="cell-sub">{n.libelleTendance}</span>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionRabattable>
      </div>

      {/* ── 2. Les erreurs relevées, avec leur lecture — ouverte (`MV-UX-02`) ── */}
      <div data-testid="manuel-erreurs">
        <SectionRabattable
          titre="Erreurs relevées"
          resume={resumeErreurs(vue.erreursRecurrentes)}
          sous={
            vue.erreursRecurrentes.length === 0
              ? undefined
              : `${resumeErreurs(vue.erreursRecurrentes)} · de la plus fréquente à la moins fréquente`
          }
          cleMemoire={cle('erreurs')}
        >
          {vue.erreursRecurrentes.length === 0 ? (
            <p className="note-line">{PHRASE_AUCUNE_ERREUR}</p>
          ) : (
            <div className="stack-sm">
              {vue.erreursRecurrentes.map((e, i) => (
                <ErreurRecurrente
                  key={e.code}
                  erreur={e}
                  rangChapitre={rang}
                  codeChapitre={chapitre.code}
                  ouverte={i === 0}
                />
              ))}
            </div>
          )}
        </SectionRabattable>
      </div>

      {/* ── 3. La préparation aux évaluations — des états ; la prochaine, ouverte ── */}
      <div data-testid="manuel-preparation">
        <SectionRabattable
          titre="Préparation aux évaluations"
          resume={resumePreparations(vue.preparations)}
          cleMemoire={cle('preparation')}
        >
          {vue.preparations.length === 0 ? (
            <p className="note-line">Aucune évaluation à venir dans l’objectif.</p>
          ) : (
            <div className="stack-sm">
              {vue.preparations.map((p, i) =>
                i === 0 ? (
                  <Preparation key={p.evaluation.id} preparation={p} rangChapitre={rang} />
                ) : (
                  /* Les évaluations suivantes : une ligne chacune, leur détail derrière. */
                  <SectionRabattable
                    key={p.evaluation.id}
                    variante="tuile"
                    titre={p.evaluation.titre}
                    resume={`${jourCivil(p.evaluation.date)} · ${dansCombien(p.evaluation.joursRestants)} · ${p.libelleEtat}`}
                    ouverteParDefaut={false}
                  >
                    <Preparation preparation={p} rangChapitre={rang} />
                  </SectionRabattable>
                ),
              )}
            </div>
          )}
        </SectionRabattable>
      </div>

      {/* ── 4. Ce que le chapitre suppose acquis ─────────────────────────── */}
      <div data-testid="manuel-prerequis">
        <SectionRabattable
          titre="Prérequis"
          resume={resumePrerequis(vue.prerequis, vue.blocagesEnAmont)}
          sous="Ce que le chapitre suppose acquis."
          ouverteParDefaut={false}
          cleMemoire={cle('prerequis')}
        >
          {vue.prerequis.length === 0 ? (
            <p className="note-line">Ce chapitre ne suppose aucun acquis d’avant.</p>
          ) : (
            <div className="list list--programme">
              {vue.prerequis.map((p) => {
                const blocages = vue.blocagesEnAmont.filter(
                  (b) => b.prerequis.code === p.notion.code,
                );
                return (
                  <div key={p.notion.code} className="li">
                    <span className="li-main">
                      <span className="li-title">{p.notion.titre}</span>
                      {p.origine === null ? null : (
                        <span className="li-sub">Vient de : {p.origine}</span>
                      )}
                      {blocages.length === 0 ? (
                        <span className="li-sub">Aucune erreur ne le met en cause.</span>
                      ) : (
                        blocages.map((b) => (
                          <span key={b.erreur.code} className="li-sub li-sub--attention">
                            Blocage en amont — mis en cause dans{' '}
                            {denombrer(b.fois, 'travail', 'travaux')} par « {b.erreur.titre} ».
                          </span>
                        ))
                      )}
                    </span>
                    {p.travaillee ? (
                      <CransNotion palier={p.palier} libelle={p.libellePalier} />
                    ) : (
                      <span className="cell-sub">Aucune trace</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </SectionRabattable>
      </div>

      {/* ── 5. La position face au programme — pour le tuteur seulement (F-15) ── */}
      <div data-testid="manuel-position">
        <SectionRabattable
          titre="Position face au programme"
          resume={resumePosition(vue.positionProgramme.attentes)}
          sous="L’élève ne voit rien de cette section."
          ouverteParDefaut={false}
          cleMemoire={cle('position')}
        >
          <h4>Compétences évaluées à l’école</h4>
          <div className="list list--programme" data-testid="manuel-competences">
            {vue.positionProgramme.competences.map((c) => (
              <div key={c.code} className="li">
                <span className="li-main">
                  <span className="li-title">
                    {c.nom} <span className="tag tag--suite">{c.partScolaire}</span>
                  </span>
                  <span className="li-sub">
                    {c.travaux === 0
                      ? 'Aucun travail ne la mesure encore.'
                      : `Mesurée par ${denombrer(c.travaux, 'travail', 'travaux')}.`}
                  </span>
                </span>
                {c.travaux === 0 ? null : (
                  <CransNotion palier={c.palier} libelle={c.libellePalier} />
                )}
              </div>
            ))}
          </div>

          <h4>Face aux attentes du programme</h4>
          {vue.positionProgramme.attentes.length === 0 ? (
            <p className="note-line">Aucune attente du programme n’est rattachée à ce chapitre.</p>
          ) : (
            <div className="list list--programme" data-testid="manuel-attentes">
              {vue.positionProgramme.attentes.map((a) => (
                <div key={a.code} className="li">
                  <span className="li-main">
                    <span className="li-title">{a.enonce}</span>
                    <span className="li-sub">
                      Attendu : {LIBELLES_PALIER_NOTION[a.palierAttendu].toLowerCase()}
                      {a.niveauExerciceAttendu === null
                        ? ''
                        : `, niveau ${a.niveauExerciceAttendu} (${NIVEAUX_EXERCICE_NOMMES[a.niveauExerciceAttendu].nom})`}
                    </span>
                    <span className="li-sub">
                      {direPreuveAttente(a, LIBELLES_PALIER_NOTION[a.palier])}
                    </span>
                  </span>
                  {/* `UX-7-35` : « En route » sur une attente à « 0 travail » disait
                      un mouvement qui n'a pas commencé. */}
                  {a.etat !== 'atteinte' && a.preuve.travaux === 0 ? (
                    <span className="cell-sub" data-testid="manuel-attente-etat">
                      {direEtatAttente(a)}
                    </span>
                  ) : (
                    <span data-testid="manuel-attente-etat">
                      <Badge
                        tone={a.etat === 'atteinte' ? 'acquis' : 'en-cours'}
                        label={direEtatAttente(a)}
                      />
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
          <p className="pal-legend">{vue.positionProgramme.mention}</p>
        </SectionRabattable>
      </div>
    </div>
  );
}

/* ─────────────────────────── une erreur qui revient ─────────────────────────── */

function ErreurRecurrente({
  erreur: e,
  rangChapitre,
  codeChapitre,
  ouverte,
}: {
  readonly erreur: ErreurRecurrenteTuteurDto;
  readonly rangChapitre: number;
  readonly codeChapitre: string;
  readonly ouverte: boolean;
}) {
  return (
    <SectionRabattable
      variante="tuile"
      titre={e.titre}
      resume={`Vue dans ${denombrer(e.fois, 'travail', 'travaux')}`}
      sous={`Vue dans ${denombrer(e.fois, 'travail', 'travaux')}, la dernière fois le ${formatDate(e.derniereLe)}${
        e.recurrente ? ' · revient sur plusieurs jours' : ''
      }`}
      ouverteParDefaut={ouverte}
      actions={
        /* La pastille ne se pose que sur une erreur qui répond à la définition
           du produit — le serveur le dit (`recurrente`), l'écran ne le déduit pas. */
        e.recurrente ? (
          <span data-testid="manuel-erreur-recurrente">
            <Badge tone="a-revoir" label="Récurrente" />
          </span>
        ) : null
      }
    >
      <dl className="lecture">
        <dt>Notion</dt>
        <dd>{nomDeNotion(e.notion, rangChapitre)}</dd>
        {e.exemple === null ? null : (
          <>
            <dt>Exemple</dt>
            <dd>
              <TexteMaths texte={e.exemple} />
            </dd>
          </>
        )}
        {e.prerequisEnCause === null ? null : (
          <>
            <dt>Prérequis en cause</dt>
            <dd>{e.prerequisEnCause.titre}</dd>
          </>
        )}
        <dt>Lecture tuteur</dt>
        <dd>
          <TexteMaths texte={e.lectureTuteur} />
        </dd>
        <dt>Que faire</dt>
        <dd>
          <TexteMaths texte={e.remediation} />
        </dd>
        <dt>Remède prévu</dt>
        <dd>
          {e.remede === null || (e.remede.fiche === null && e.remede.capsule === null) ? (
            'Aucun renvoi.'
          ) : (
            <span className="row wrap">
              {e.remede.fiche === null ? null : (
                <Link
                  className="relecture-lien"
                  to={adresses.fiche(codeChapitre, e.remede.fiche.code, e.remede.fiche.ancre)}
                >
                  Fiche, au piège : {e.remede.fiche.titre}
                </Link>
              )}
              {e.remede.capsule === null ? null : (
                <Link
                  className="relecture-lien"
                  to={adresses.capsule(codeChapitre, e.remede.capsule.code)}
                >
                  Capsule : {e.remede.capsule.titre}
                </Link>
              )}
            </span>
          )}
        </dd>
      </dl>
    </SectionRabattable>
  );
}

/* ─────────────────────────── une évaluation à venir ─────────────────────────── */

function Preparation({
  preparation: p,
  rangChapitre,
}: {
  readonly preparation: PreparationEvaluationDto;
  readonly rangChapitre: number;
}) {
  const { evaluation } = p;
  return (
    <div className="preparation">
      <div className="row-between wrap">
        <div>
          <strong>{evaluation.titre}</strong>
          <span className="cell-sub">
            {evaluation.libelleNature} · {jourCivil(evaluation.date)} ·{' '}
            {dansCombien(evaluation.joursRestants)}
          </span>
        </div>
        <Badge tone={TON_PREPARATION[p.etat]} label={p.libelleEtat} />
      </div>
      <div className="list list--programme">
        {p.parNotion.map((n) => (
          <div key={n.notion.code} className="li">
            <span className="li-main">
              <span className="li-title">{nomDeNotion(n.notion, rangChapitre)}</span>
            </span>
            {n.couverte ? (
              <CransNotion palier={n.palier} libelle={n.libellePalier} />
            ) : (
              <span className="cell-sub">Non couverte</span>
            )}
          </div>
        ))}
      </div>
      {/* « Réaliste d'ici là, dans l'ordre » — le plan daté — est retiré
          (fondateur, 20 oct. 2026, AB#977) : l'élève se prépare par un
          parcours. */}
    </div>
  );
}
